import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { clearCurrentUser } from "../hooks/useCurrentUser";
import { useAppStore } from "../store/appStore";
import { APP_VERSION } from "../version";
import { IconAudit, IconDashboard, IconLogout, IconOperators, IconSearch, IconTenants } from "./Icons";

type IconType = (props: { size?: number; className?: string }) => JSX.Element;

interface NavItem {
  to: string;
  label: string;
  icon?: IconType;
  end?: boolean;
  children?: NavItem[];
}

function getBaseNav(t: (key: string) => string): NavItem[] {
  return [
    { to: "/", label: t("nav.dashboard"), icon: IconDashboard, end: true },
    { to: "/tenants", label: t("nav.tenants"), icon: IconTenants },
    { to: "/operators", label: t("nav.operators"), icon: IconOperators },
    { to: "/audit-logs", label: t("nav.auditLogs"), icon: IconAudit },
  ];
}

function getTenantNav(tenantId: string, t: (key: string) => string): NavItem {
  return {
    to: `/tenants/${tenantId}`,
    label: t("nav.tenantDetail"),
    children: [
      { to: `/tenants/${tenantId}`, label: t("nav.tenantDetail"), end: true },
      { to: `/tenants/${tenantId}/users`, label: t("nav.tenantUsers"), end: true },
      { to: `/tenants/${tenantId}/data/branches`, label: t("nav.branches"), end: true },
      { to: `/tenants/${tenantId}/data/warehouses`, label: t("nav.warehouses"), end: true },
      { to: `/tenants/${tenantId}/data/products`, label: t("nav.products"), end: true },
      { to: `/tenants/${tenantId}/data/categories`, label: t("nav.categories"), end: true },
      { to: `/tenants/${tenantId}/data/tags`, label: t("nav.tags"), end: true },
      { to: `/tenants/${tenantId}/data/vat-rates`, label: t("nav.vatRates"), end: true },
      { to: `/tenants/${tenantId}/data/orders`, label: t("nav.orders"), end: true },
      { to: `/tenants/${tenantId}/data/customers`, label: t("nav.customers"), end: true },
      { to: `/tenants/${tenantId}/data/employees`, label: t("nav.employees"), end: true },
      { to: `/tenants/${tenantId}/data/devices`, label: t("nav.devices"), end: true },
      { to: `/tenants/${tenantId}/data/licenses`, label: t("nav.licenses"), end: true },
      { to: `/tenants/${tenantId}/data/invoices`, label: t("nav.invoices"), end: true },
      { to: `/tenants/${tenantId}/data/eet-receipts`, label: t("nav.eetReceipts"), end: true },
      { to: `/tenants/${tenantId}/data/shifts`, label: t("nav.shifts"), end: true },
      { to: `/tenants/${tenantId}/data/attendance`, label: t("nav.attendance"), end: true },
      { to: `/tenants/${tenantId}/data/inventory`, label: t("nav.inventory"), end: true },
      { to: `/tenants/${tenantId}/data/stock-receipts`, label: t("nav.stockReceipts"), end: true },
    ],
  };
}

function isActivePath(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function NavGroup({ item, open, onToggle }: { item: NavItem; open: boolean; onToggle: () => void }) {
  const Icon = item.icon;
  return (
    <div>
      <button
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          padding: "10px 14px",
          borderRadius: "var(--radius-sm)",
          background: open ? "var(--accent-dim)" : "transparent",
          color: open ? "var(--accent)" : "var(--text-secondary)",
          border: "none",
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {Icon && <Icon size={20} />}
        <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
        <span style={{ fontSize: 10 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ paddingLeft: 18, marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
          {(item.children ?? []).map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              end={c.end}
              style={({ isActive }) => ({
                display: "block",
                padding: "8px 14px 8px 28px",
                borderRadius: "var(--radius-sm)",
                color: isActive ? "var(--accent)" : "var(--text-secondary)",
                background: isActive ? "var(--accent-dim)" : "transparent",
                fontWeight: isActive ? 600 : 400,
                fontSize: 13,
                textDecoration: "none",
                borderLeft: isActive ? "2px solid var(--accent)" : "2px solid transparent",
              })}
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function NavLinkItem({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        borderRadius: "var(--radius-sm)",
        color: isActive ? "var(--accent)" : "var(--text-secondary)",
        background: isActive ? "var(--accent-dim)" : "transparent",
        fontWeight: isActive ? 600 : 500,
        fontSize: 14,
        textDecoration: "none",
      })}
    >
      {Icon && <Icon size={20} />}
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const user = useAppStore((s) => s.user);
  const tenantId = useAppStore((s) => s.tenantId);
  const tenant = useAppStore((s) => s.tenant);
  const setUser = useAppStore((s) => s.setUser);

  const baseNav = getBaseNav(t);
  const tenantNav = tenantId ? getTenantNav(tenantId, t) : null;

  const [openTenant, setOpenTenant] = useState(() =>
    tenantNav ? isActivePath(location.pathname, `/tenants/${tenantId}`) : false
  );

  useEffect(() => {
    if (tenantNav) {
      setOpenTenant(isActivePath(location.pathname, `/tenants/${tenantId}`));
    }
  }, [location.pathname, tenantId, tenantNav]);

  async function logout() {
    const rt = localStorage.getItem("lk_ops_refresh");
    if (rt) {
      api.post("/auth/logout", { refresh_token: rt }).catch(() => {});
    }
    localStorage.removeItem("lk_ops_access");
    localStorage.removeItem("lk_ops_refresh");
    clearCurrentUser();
    setUser(null);
    navigate("/login");
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)" }}>
      <aside
        style={{
          width: "var(--sidebar-w)",
          flexShrink: 0,
          background: "var(--bg-elevated)",
          borderRight: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          position: "sticky",
          top: 0,
          height: "100vh",
        }}
      >
        <div
          style={{
            padding: "20px 20px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            minHeight: 64,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 18 }}>LuigiPOS Ops</span>
        </div>

        <nav
          style={{
            flex: 1,
            padding: "12px 10px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: "auto",
          }}
        >
          {baseNav.map((n) => (
            <NavLinkItem key={n.to} item={n} />
          ))}
          {tenantNav && (
            <>
              <hr style={{ margin: "12px 0" }} />
              <div
                style={{
                  padding: "0 14px 8px",
                  fontSize: 12,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("nav.selectedTenant")}
              </div>
              <NavGroup
                item={{ ...tenantNav, label: tenant?.name ?? t("nav.tenantDetail") }}
                open={openTenant}
                onToggle={() => setOpenTenant((v) => !v)}
              />
            </>
          )}
        </nav>

        <div style={{ padding: "12px 10px", borderTop: "1px solid var(--border)" }}>
          <button
            onClick={logout}
            className="ghost"
            style={{
              width: "100%",
              justifyContent: "flex-start",
              gap: 12,
              color: "var(--muted)",
            }}
          >
            <IconLogout size={20} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>

        <div
          style={{
            padding: "8px 16px",
            color: "var(--muted)",
            fontSize: 11,
            textAlign: "center",
          }}
        >
          Ops Center v{APP_VERSION}
        </div>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header
          style={{
            minHeight: 64,
            borderBottom: "1px solid var(--border)",
            background: "var(--bg-elevated)",
            display: "flex",
            alignItems: "center",
            padding: "0 28px",
            gap: 16,
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              background: "var(--bg)",
              border: "1px solid var(--border)",
              borderRadius: 24,
              flex: 1,
              maxWidth: 320,
              minWidth: 140,
            }}
          >
            <IconSearch size={18} className="muted" style={{ position: "absolute", left: 14, pointerEvents: "none" }} />
            <input
              placeholder={t("common.search")}
              style={{
                border: "none",
                background: "transparent",
                padding: "9px 16px 9px 42px",
                boxShadow: "none",
                flex: 1,
                fontSize: 14,
                color: "var(--text)",
                outline: "none",
                minWidth: 0,
                borderRadius: 24,
              }}
            />
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="text-secondary" style={{ fontSize: 13 }}>
              {user?.full_name || user?.email}
            </span>
            <span className="badge badge-accent" style={{ fontSize: 11 }}>
              {user?.role}
            </span>
          </div>
        </header>

        <main style={{ flex: 1, padding: "32px 28px", overflow: "auto", minWidth: 0 }}>
          <div className="fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
